import { Injectable } from '@angular/core';

const UPLOADS_DIR = 'uploads';

@Injectable({ providedIn: 'root' })
export class OpfsService {
  private uploadsDir: FileSystemDirectoryHandle | null = null;

  async init(): Promise<void> {
    if (this.uploadsDir) {
      return;
    }
    const root = await navigator.storage.getDirectory();
    this.uploadsDir = await this.resolveUploadsDir(root);
  }

  async writeFile(name: string, data: FileSystemWriteChunkType): Promise<void> {
    const dir = await this.ensureUploads();
    const fileHandle = await dir.getFileHandle(name, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(data);
    await writable.close();
  }

  async getFile(name: string): Promise<File> {
    const dir = await this.ensureUploads();
    const fileHandle = await dir.getFileHandle(name);
    return fileHandle.getFile();
  }

  /** Whether a file persisted under `name` is still available on local disk. */
  async exists(name: string): Promise<boolean> {
    try {
      const dir = await this.ensureUploads();
      await dir.getFileHandle(name);
      return true;
    } catch {
      return false;
    }
  }

  /** Names of all files persisted under the uploads directory. */
  async listFiles(): Promise<string[]> {
    const dir = await this.ensureUploads();
    const iterable = (dir as unknown as { keys(): AsyncIterable<string> }).keys();
    const names: string[] = [];
    for await (const name of iterable) {
      names.push(name);
    }
    return names;
  }

  async deleteFile(name: string): Promise<void> {
    const dir = await this.ensureUploads();
    await dir.removeEntry(name);
  }

  async clearUploads(): Promise<void> {
    const dir = await this.ensureUploads();
    const iterable = (dir as unknown as { keys(): AsyncIterable<string> }).keys();
    for await (const name of iterable) {
      await dir.removeEntry(name, { recursive: true });
    }
  }

  private async resolveUploadsDir(root: FileSystemDirectoryHandle): Promise<FileSystemDirectoryHandle> {
    try {
      return await root.getDirectoryHandle(UPLOADS_DIR);
    } catch {
      return root.getDirectoryHandle(UPLOADS_DIR, { create: true });
    }
  }

  private async ensureUploads(): Promise<FileSystemDirectoryHandle> {
    if (!this.uploadsDir) {
      await this.init();
    }
    return this.uploadsDir as FileSystemDirectoryHandle;
  }
}
